import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useQueryClient } from 'react-query'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BrandPlanSchedule } from 'types'

function markScheduleContentViewed(schedule: BrandPlanSchedule, fileKey: string, viewedAt: string): BrandPlanSchedule {
  let changed = false
  const contents = schedule?.contents?.map((content) => {
    if (content.almanac_file?.key !== fileKey) return content

    changed = true
    return {
      ...content,
      almanac_file: content.almanac_file ? { ...content.almanac_file, viewed_at: viewedAt } : content.almanac_file,
    }
  })

  if (!changed) return schedule

  return { ...schedule, contents }
}

export const useMarkFileAsViewed = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const markFileAsViewed = (almanacFileKey?: string | null) => {
    if (!almanacFileKey) return

    const viewedAt = new Date().toISOString()

    // The schedule list cache (`['workspaces', workspaceId, 'schedules', 'list']`)
    // holds `BrandPlanScheduleListItem`s whose `contents` are slim
    // ({ id, type, status }) and don't expose `almanac_file`, so there's no
    // per-content `viewed_at` to patch in the list cache. The detail cache
    // below is the source of truth for the optimistic view-state update.

    const calendarQueries = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(calendarKeys.all)
    const fileQueries = queryClient.getQueriesData<AlmanacFileShowItem[]>(filesKeys.all(workspaceId))

    calendarQueries.forEach(([queryKey, oldData]) => {
      if (!oldData || !Array.isArray(oldData)) return

      const newData = oldData.map((item) => {
        if (item.key === almanacFileKey) {
          return {
            ...item,
            viewed_at: viewedAt,
          }
        }
        return item
      })

      queryClient.setQueryData(queryKey, newData)
    })

    fileQueries.forEach(([queryKey]) => {
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData || !Array.isArray(oldData)) return oldData

        const newData = oldData?.map((file: AlmanacFileShowItem) => {
          if (file.key === almanacFileKey) return { ...file, viewed_at: viewedAt }

          return file
        })

        return newData
      })
    })

    queryClient.setQueriesData<BrandPlanSchedule | undefined>(['schedules', 'detail'], (old) => {
      if (!old) return old
      return markScheduleContentViewed(old, almanacFileKey, viewedAt)
    })
  }

  return { markFileAsViewed }
}
